/**
 * Utilidades helper para el widget TIA GPC
 */

import { BREAKPOINTS } from './constants';
import { CAPTCHA_STORAGE_KEYS, CAPTCHA_VALIDITY_MS } from '../config/security';

/**
 * Verifica si el dispositivo es móvil
 * @returns {boolean}
 */
export const isMobile = () => {
  return window.innerWidth < BREAKPOINTS.MOBILE;
};

/**
 * Verifica si el dispositivo es tablet
 * @returns {boolean}
 */
export const isTablet = () => {
  return window.innerWidth >= BREAKPOINTS.MOBILE && window.innerWidth < BREAKPOINTS.TABLET;
};

/**
 * Formatea un timestamp ISO a formato legible
 * @param {string} isoString - Timestamp en formato ISO
 * @returns {string} Formato legible (HH:MM)
 */
export const formatTimestamp = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

/**
 * Formatea milisegundos a formato legible
 * @param {number} ms - Milisegundos
 * @returns {string} Formato legible (ej: "1.5s", "234ms")
 */
export const formatResponseTime = (ms) => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.round(ms)}ms`;
};

/**
 * Genera un ID único
 * @returns {string} UUID v4
 */
export const generateId = () => {
  return crypto.randomUUID();
};

/**
 * Clasifica el error HTTP y devuelve mensaje traducible
 * @param {number} statusCode - Código de estado HTTP
 * @param {string} errorCode - Código de error del backend (opcional)
 * @returns {string} Clave de traducción para el error
 */
export const getErrorKey = (statusCode, errorCode = null) => {
  // Primero verificar si hay un error code específico del backend
  if (errorCode === 'NO_KNOWLEDGE_AVAILABLE') {
    return 'errors.noKnowledgeAvailable';
  }
  if (errorCode === 'INSUFFICIENT_CREDITS') {
    return 'errors.insufficientCredits';
  }

  // Si no, usar el status code HTTP
  switch (statusCode) {
    case 401:
      return 'errors.session';
    case 402:
      return 'errors.insufficientCredits';
    case 403:
      return 'errors.validation';
    case 422:
      return 'errors.noKnowledgeAvailable';
    case 429:
      return 'errors.rateLimit';
    case 500:
    case 502:
    case 503:
      return 'errors.network';
    default:
      return 'errors.generic';
  }
};

/**
 * Obtiene el tema del sistema operativo
 * @returns {'light'|'dark'} Tema del sistema
 */
export const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

/**
 * Sanitiza el input del usuario para prevenir inyección
 * @param {string} input - Input del usuario
 * @returns {string} Input sanitizado
 */
export const sanitizeInput = (input) => {
  return input.trim().slice(0, 2000);
};

/**
 * Valida si una string es un UUID válido
 * @param {string} uuid - UUID a validar
 * @returns {boolean}
 */
export const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Verifica si el CAPTCHA ha sido completado y sigue vigente
 * @returns {boolean}
 */
export const isCaptchaValid = () => {
  const status = localStorage.getItem(CAPTCHA_STORAGE_KEYS.VERIFIED);
  const timestamp = localStorage.getItem(CAPTCHA_STORAGE_KEYS.TIMESTAMP);
  return (
    status === 'true' &&
    !!timestamp &&
    Date.now() - parseInt(timestamp, 10) < CAPTCHA_VALIDITY_MS
  );
};

/**
 * Convierte un logo base64 a una URL de datos válida para <img>
 * @param {string|null|undefined} logoBase64
 * @returns {string|null}
 */
export const getLogoSrc = (logoBase64) => {
  if (!logoBase64) return null;
  return logoBase64.startsWith('data:')
    ? logoBase64
    : `data:image/png;base64,${logoBase64}`;
};

/**
 * Extrae el prefijo de la licencia para usar como clave de storage
 * @param {string} licenseToken
 * @returns {string}
 */
export const licenseKeyPrefix = (licenseToken) => licenseToken.substring(0, 16);

/**
 * Debounce function para optimizar eventos frecuentes
 * @param {Function} func - Función a debounce
 * @param {number} wait - Tiempo de espera en ms
 * @returns {Function}
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
